import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { parseParams, stringifyParams } from "~/util/queryString";
import removeEmptyObjectKeys from "~/util/removeEmptyObjectKeys";

/**
 * @type QueryParamValue
 * @description Type for individual URL parameter values
 */
type QueryParamValue =
  | string
  | number
  | boolean
  | object
  | null
  | undefined
  | QueryParamValue[]
  | { [key: string]: QueryParamValue };

/**
 * @interface Params
 * @description Interface for the URL parameters
 */
interface Params {
  [key: string]: QueryParamValue;
}

/**
 * @function useUrlParams
 * @description Custom hook to get and set URL parameters
 * @returns {object} - The URL parameters and a function to set them
 */
const useUrlParams = (): {
  clearAllParams: () => void;
  params: Params;
  setUrlParams: (params: Params) => void;
} => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const stringParams = searchParams.toString();

  /**
   * @function setUrlParams
   * @description Function to add new parameters to the URL
   * @param {Params} params - The new parameters to add
   * @note This will overwrite any existing parameters with the same key
   */
  const setUrlParams = useCallback(
    (params: Params) => {
      const currentParams = parseParams(stringParams);

      // Check if there are any differences between currentParams and params
      const hasChanges = (() => {
        // Check all keys in new params
        for (const key of Object.keys(params)) {
          if (
            typeof params[key] === "object" &&
            params[key] !== null &&
            !Array.isArray(params[key])
          ) {
            // Handle nested objects
            const newSubObj = params[key] as Record<string, unknown>;
            const currentSubObj =
              currentParams[key] &&
              typeof currentParams[key] === "object" &&
              currentParams[key] !== null &&
              !Array.isArray(currentParams[key])
                ? (currentParams[key] as Record<string, unknown>)
                : {};

            // Check if any sub-keys are different
            for (const subKey of Object.keys(newSubObj)) {
              if (newSubObj[subKey] !== currentSubObj[subKey]) {
                return true;
              }
            }
          } else {
            // Handle primitive values
            if (params[key] !== currentParams[key]) {
              return true;
            }
          }
        }

        // Check if any keys were removed (exist in current but not in new)
        for (const key of Object.keys(currentParams)) {
          if (!(key in params)) {
            return true;
          }
        }

        return false;
      })();

      if (!hasChanges) {
        // If there are no changes, do nothing
        return;
      }

      // Add the new params.
      let newParams = { ...currentParams, ...params };

      // Remove any empty object keys.
      newParams = removeEmptyObjectKeys(newParams);

      setSearchParams(stringifyParams(newParams));
    },
    [stringParams, setSearchParams]
  );

  const clearAllParams = useCallback(() => {
    const fullUrl = `${pathname}`;
    navigate(fullUrl);
  }, [pathname, navigate]);

  const params: Params = parseParams(stringParams);

  return { clearAllParams, params, setUrlParams };
};

export default useUrlParams;
